import { forwardRef, ForwardRefExoticComponent, RefAttributes, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Avatar, Badge, Box, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { LockOutlined } from '@ant-design/icons';

// project import
import { activeItem } from 'store/reducers/menu';
import { useUpgradeDrawer } from '../../../../../contexts/UpgradeDrawerContext';
import { useTour } from '../../../../../contexts/TourContext';
import { NAV_ANCHOR_BY_MENU_ID } from '../../../../../sections/welcome-tour/tourAnchors';
import { TOUR_Z } from '../../../../../sections/welcome-tour/tourZIndex';
import { ACCENT as TOUR_ACCENT } from '../../../../../sections/welcome-tour/tourTheme';

// types
import { LinkTarget, NavItemType } from 'types/menu';
import { RootStateProps } from 'types/root';
import { textSizeByBreakpoint } from '../../../../../utils/helper';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

interface Props {
  item: NavItemType;
}

const NavItem = ({ item }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const menu = useSelector((state: RootStateProps) => state.menu);
  const { drawerOpen } = menu;
  const { active: tourActive, unlockedMenuId } = useTour();
  const { openUpgradeDrawer } = useUpgradeDrawer();

  // While the tour points at a menu item, every other item is inert — the user
  // can only go where the step is sending them.
  const tourTarget = tourActive && !!unlockedMenuId && item.id === unlockedMenuId;
  const tourLocked = tourActive && !!unlockedMenuId && !tourTarget;
  const tourAnchor = item.id ? NAV_ANCHOR_BY_MENU_ID[item.id] : undefined;

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  // Memoized: a fresh forwardRef component every render gives React a new
  // component identity, so it UNMOUNTED and remounted the whole item on every
  // parent render — churning the DOM (and killing any element reference the
  // tour spotlight was holding).
  const listItemProps = useMemo<{
    component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
    href?: string;
    target?: LinkTarget;
  }>(
    () =>
      item?.external
        ? { component: 'a', href: item.url, target: itemTarget }
        : { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url!} target={itemTarget} />) },
    [item?.external, item.url, itemTarget]
  );

  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon sx={{ color: 'inherit' }} /> : false;

  // let isSelected = openItem.findIndex((id) => id === item.id) > -1;

  const [isSelected, setIsSelected] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    const selected = pathname?.includes(item.url || '');
    setIsSelected(selected);
  }, [pathname]);

  // active menu item on page load
  useEffect(() => {
    if (pathname && pathname.includes('product-details')) {
      if (item.url && item.url.includes('product-details')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);
  const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'navItem.main';
  const iconSelectedColor = 'primary.main';
  const disabledColor = theme.palette.mode === 'dark' ? 'grey.600' : 'grey.400';

  // Plan-gated items. Free-locked ones stay REAL links — no hover tooltip,
  // no click hijack: the destination page carries the subscribe CTA, and the
  // padlock chip alone marks the state here. (The old controlled tooltip
  // re-fired on every hover pass, which is what made it feel broken.)
  const isLocked = item.isAnalyzerLocked || item.isFreeUserLocked;

  const handleOnClick = (e: any, item: any) => {
    if (item.isAnalyzerLocked) {
      // Analyzer routes sit behind AnalyzerGuard, which bounces non-role
      // users to the dashboard BEFORE any page CTA could render — letting
      // the link through is a silent dead end. Open checkout instead (the
      // Analyzer add-on lives in the configure step). No tooltip.
      e.preventDefault();
      openUpgradeDrawer('analytics');
      return;
    }
    if (item.disabled) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Tooltip placement={'right'} title={!drawerOpen ? item.title : ''} disableHoverListener={drawerOpen}>
        <span style={{ display: 'block', cursor: item.disabled ? 'not-allowed' : 'inherit' }}>
          <ListItemButton
            {...listItemProps}
            data-tour={tourAnchor}
            disabled={item.disabled || tourLocked}
            selected={isSelected}
            onClick={(e) => {
              handleOnClick(e, item);
            }}
            sx={{
              pointerEvents: tourLocked ? 'none' : isLocked ? 'auto' : item.disabled ? 'none' : 'auto',
              opacity: tourLocked ? 0.3 : 1,
              // Lift the target above the tour's dim so it reads as the only
              // live control on screen, and ring it so the eye lands there.
              zIndex: tourTarget ? TOUR_Z.target : 1201,
              pl: drawerOpen ? '10px' : 1.5,
              py: '5px',
              mx: drawerOpen ? '20px' : 0, // All items have 20px margin (inside the 30px sidebar padding)
              borderRadius: drawerOpen ? 2 : 0,
              border: '1px solid transparent',
              // Follow the drawer's collapse curve — these values swap with
              // drawerOpen and snapped a frame ahead of the width animation.
              transition: (t) =>
                t.transitions.create(['padding-left', 'margin', 'border-radius'], {
                  duration: 280,
                  easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
                }),
              // The one live control while a step points here: tinted fill,
              // accent border and a pulsing ring, with the label and icon
              // forced to the accent so the row reads as "click this".
              ...(tourTarget && {
                position: 'relative',
                backgroundColor: alpha(TOUR_ACCENT, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                border: `1px solid ${TOUR_ACCENT}`,
                boxShadow: `0 0 0 0 ${TOUR_ACCENT}66`,
                animation: 'tcRing 1.6s ease-out infinite',
                '& .MuiListItemIcon-root, & .MuiTypography-root': {
                  color: `${TOUR_ACCENT} !important`,
                  fontWeight: 700
                },
                '&:hover': { backgroundColor: alpha(TOUR_ACCENT, theme.palette.mode === 'dark' ? 0.22 : 0.16) },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
              }),
              ...(drawerOpen &&
                !isLocked && {
                  '&:hover': {
                    border: `1px solid ${theme.palette.border.main}`,
                    boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2
                  },
                  '&.Mui-selected': {
                    border: `1px solid ${theme.palette.border.main}`,
                    boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                    bgcolor: theme.palette.background.paper,
                    color: iconSelectedColor,
                    borderRadius: 2,
                    '&:hover': {
                      border: `1px solid ${theme.palette.border.main}`,
                      boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                      color: iconSelectedColor,
                      bgcolor: theme.palette.background.paper
                    }
                  }
                }),
              ...(drawerOpen &&
                isLocked && {
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : alpha('#0b1512', 0.03),
                    border: '1px solid transparent'
                  },
                  '&:hover .nav-lock-badge': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.1)
                  }
                }),
              ...(!drawerOpen && {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  bgcolor: 'transparent'
                }
              })
            }}
          >
            {itemIcon && !drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 16,
                  marginRight: 1,
                  color: isSelected ? iconSelectedColor : textColor,
                  ...(!drawerOpen && {
                    borderRadius: 1.5,
                    width: 36,
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'secondary.lighter',
                      color: 'primary.main'
                    }
                  }),
                  ...(!drawerOpen &&
                    isSelected && {
                      bgcolor: theme.palette.mode === 'dark' ? 'primary.900' : 'primary.lighter',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'primary.darker' : 'primary.lighter'
                      }
                    })
                }}
              >
                {item.badgeValue && (
                  <Badge color={item.badgeColor} badgeContent={item.badgeValue}>
                    {itemIcon}
                  </Badge>
                )}
                {!item.badgeValue && isLocked && (
                  // Collapsed rail: no room for text, so the padlock rides the
                  // icon's corner to keep the locked state legible.
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={<LockOutlined style={{ fontSize: 9 }} />}
                    sx={{ '& .MuiBadge-badge': { minWidth: 14, height: 14, p: 0, color: disabledColor, bgcolor: 'transparent' } }}
                  >
                    {itemIcon}
                  </Badge>
                )}
                {/* Collapsed rail: no room for the NEW chip, so a dot on the icon stands in for it. */}
                {!item.badgeValue &&
                  !isLocked &&
                  (item.isNew ? (
                    <Badge variant="dot" color="primary" overlap="circular">
                      {itemIcon}
                    </Badge>
                  ) : (
                    itemIcon
                  ))}
              </ListItemIcon>
            )}
            {itemIcon && drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 20,
                  marginRight: 1,
                  color: isSelected ? iconSelectedColor : textColor
                }}
              >
                {item.badgeValue && (
                  <Badge color={item.badgeColor} badgeContent={item.badgeValue}>
                    {itemIcon}
                  </Badge>
                )}
                {!item.badgeValue && itemIcon}
              </ListItemIcon>
            )}
            {drawerOpen && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: isSelected ? iconSelectedColor : textColor,
                      fontSize: textSizeByBreakpoint(15),
                      fontWeight: isSelected ? 700 : 500,
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {item.title}
                  </Typography>
                }
              />
            )}
            {/* The padlock marks the plan-gated state; it neither dims nor disables the
                row. Analyzer items open checkout on click; free-locked items stay
                real links, and the destination page's PlanRequiredBanner carries
                the ask. */}
            {drawerOpen && isLocked && (
              <Box
                className="nav-lock-badge"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: '7px',
                  fontSize: 11,
                  color: disabledColor,
                  bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.05) : alpha('#0b1512', 0.05),
                  transition: 'color 160ms ease, background-color 160ms ease'
                }}
              >
                <LockOutlined />
              </Box>
            )}
            {drawerOpen && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>
        </span>
      </Tooltip>
    </>
  );
};

export default NavItem;
